<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { fade } from 'svelte/transition';

  import type { Playlist, PlaylistItem } from './api';
  import { recordPlayback } from './playback-tracker';
  import { precacheMedia } from './media-cache';

  export let playlist: Playlist;

  let currentIndex = 0;
  let currentItem: PlaylistItem | null = null;
  let itemStartedAt = Date.now();
  let advanceTimer: ReturnType<typeof setTimeout> | null = null;

  $: items = playlist.items;
  $: if (items.length > 0) {
    // Reset al recibir nueva playlist
    currentIndex = currentIndex % items.length;
    showItem(currentIndex);
  }

  function showItem(index: number) {
    if (items.length === 0) {
      currentItem = null;
      return;
    }

    // Si había un item anterior, registrar su playback
    if (currentItem) {
      const playedSec = Math.floor((Date.now() - itemStartedAt) / 1000);
      recordPlayback({
        mediaAssetId: currentItem.mediaAssetId,
        playlistId: playlist.id,
        playedAt: new Date(itemStartedAt).toISOString(),
        durationPlayedSeconds: playedSec,
        completed: playedSec >= currentItem.durationSeconds,
      });
    }

    currentIndex = (index + items.length) % items.length;
    currentItem = items[currentIndex] ?? null;
    itemStartedAt = Date.now();

    if (!currentItem) return;

    // Programar el siguiente.
    if (advanceTimer) clearTimeout(advanceTimer);
    if (currentItem.mediaAsset.type === 'VIDEO') {
      // Para VIDEO no avanzamos por reloj: dejamos que se reproduzca entero y
      // avanzamos en `on:ended`. Si usáramos un timer fijo = duración, arrancaría
      // en t=0 pero el video tarda en cargar (negro), y el timer cortaría la cola
      // justo esos segundos. El timer queda solo como red de seguridad MUY holgada
      // por si el video se cuelga y nunca dispara `ended`.
      const safetyMs = (currentItem.durationSeconds + 30) * 1000;
      advanceTimer = setTimeout(() => showItem(currentIndex + 1), safetyMs);
    } else {
      // Imágenes: se pintan al instante → timer fijo = duración.
      const durationMs = currentItem.durationSeconds * 1000;
      advanceTimer = setTimeout(() => showItem(currentIndex + 1), durationMs);
    }

    // Pre-cache de los próximos 3 items
    const nextUrls: string[] = [];
    for (let i = 1; i <= 3; i++) {
      const next = items[(currentIndex + i) % items.length];
      if (next) nextUrls.push(next.mediaAsset.cdnUrl);
    }
    void precacheMedia(nextUrls);
  }

  onMount(() => {
    showItem(0);
  });

  onDestroy(() => {
    if (advanceTimer) clearTimeout(advanceTimer);
    // Registrar último playback al destruir
    if (currentItem) {
      const playedSec = Math.floor((Date.now() - itemStartedAt) / 1000);
      recordPlayback({
        mediaAssetId: currentItem.mediaAssetId,
        playlistId: playlist.id,
        playedAt: new Date(itemStartedAt).toISOString(),
        durationPlayedSeconds: playedSec,
        completed: false,
      });
    }
  });

  function handleVideoPlaying() {
    // El video ARRANCÓ de verdad (ya no está en negro cargando). Recién ahora
    // contamos su duración, así el reloj no se come la cola por el tiempo de
    // carga. No dependemos solo de `ended` (poco fiable en WebKit viejo).
    if (!currentItem || currentItem.mediaAsset.type !== 'VIDEO') return;
    if (advanceTimer) clearTimeout(advanceTimer);
    advanceTimer = setTimeout(
      () => showItem(currentIndex + 1),
      currentItem.durationSeconds * 1000,
    );
  }

  function handleVideoEnded() {
    // Video terminó antes del reloj → avanzamos ya
    showItem(currentIndex + 1);
  }

  function handleVideoError() {
    // Video roto/no reproduce: no lo dejamos en negro toda la red de seguridad.
    // Esperamos 4s (evita loop apretado si es el único item) y avanzamos.
    if (advanceTimer) clearTimeout(advanceTimer);
    advanceTimer = setTimeout(() => showItem(currentIndex + 1), 4000);
  }
</script>

<div class="carousel">
  {#if currentItem}
    {#key currentItem.id}
      <div class="slide" transition:fade={{ duration: 600 }}>
        {#if currentItem.mediaAsset.type === 'VIDEO'}
          <video
            src={currentItem.mediaAsset.cdnUrl}
            autoplay
            muted
            playsinline
            on:playing={handleVideoPlaying}
            on:ended={handleVideoEnded}
            on:error={handleVideoError}
          />
        {:else}
          <img src={currentItem.mediaAsset.cdnUrl} alt="" />
        {/if}
      </div>
    {/key}
  {/if}
</div>

<style>
  .carousel {
    position: absolute;
    inset: 0;
    background: #000;
  }
  .slide {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .slide img,
  .slide video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
</style>
